import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSidenav } from '@angular/material/sidenav';
import { AuthService } from '@core/auth/auth.service';

interface MenuItem {
  label: string;
  icon: string;
  route: string;
  requiredRole?: string;
  requiredRoles?: string[];
}

interface MenuGroup {
  id: string;
  label: string;
  icon: string;
  items: MenuItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatListModule,
    MatIconModule,
    MatDividerModule,
    MatExpansionModule
  ],
  template: `
    <div class="sidebar-content">
      <div class="logo-section">
        <img class="logo-img" src="assets/images/logo.png" alt="Aethria Studio">
        <h2>AETHRIA STUDIO</h2>
        <span class="brand-subtitle">Business · Inventaire · Work</span>
      </div>

      <mat-divider></mat-divider>

      <mat-accordion class="nav-accordion" displayMode="flat" [multi]="true">
        <mat-expansion-panel
          *ngFor="let group of visibleGroups"
          [expanded]="isGroupExpanded(group)"
          class="nav-group"
        >
          <mat-expansion-panel-header>
            <mat-panel-title>
              <mat-icon>{{ group.icon }}</mat-icon>
              <span>{{ group.label }}</span>
            </mat-panel-title>
          </mat-expansion-panel-header>

          <mat-nav-list>
            <a
              *ngFor="let item of visibleItems(group)"
              mat-list-item
              [routerLink]="item.route"
              routerLinkActive="active"
              [routerLinkActiveOptions]="item.route === '/inventaire' || item.route === '/business' || item.route === '/work' ? { exact: true } : { exact: false }"
              (click)="onMenuClick()"
            >
              <mat-icon matListItemIcon>{{ item.icon }}</mat-icon>
              <span matListItemTitle>{{ item.label }}</span>
            </a>
          </mat-nav-list>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
  `,
  styles: [`
    .sidebar-content {
      height: 100%;
      display: flex;
      flex-direction: column;
      background: var(--bg-sidebar);
      color: var(--text);
    }

    .logo-section {
      padding: 28px 20px 20px;
      text-align: center;
    }

    .logo-img {
      width: 56px;
      height: 56px;
      object-fit: contain;
      border-radius: 12px;
      filter: drop-shadow(0 0 14px var(--accent-glow));
    }

    h2 {
      margin: 12px 0 0 0;
      font-family: var(--font-mono);
      font-size: 15px;
      font-weight: 700;
      letter-spacing: 0.2em;
      color: var(--accent);
    }

    .brand-subtitle {
      display: block;
      margin-top: 4px;
      font-size: 10.5px;
      font-weight: 300;
      color: var(--text-muted);
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .nav-accordion {
      padding: 8px 0 24px;
    }

    .nav-group {
      background: transparent !important;
      box-shadow: none !important;
    }

    .nav-group ::ng-deep .mat-expansion-panel-body {
      padding: 0 0 8px;
    }

    .nav-group ::ng-deep .mat-expansion-panel-header {
      padding: 0 16px;
      height: 48px;
    }

    mat-panel-title {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--text-muted);

      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: var(--accent);
      }
    }

    a.active {
      background-color: var(--accent-glow) !important;
      color: var(--accent) !important;
      border-left: 3px solid var(--accent);
    }

    a.active mat-icon {
      color: var(--accent) !important;
    }
  `]
})
export class SidebarComponent {
  @Input() drawer!: MatSidenav;
  @Input() isHandset: boolean | null = false;

  private authService = inject(AuthService);
  private router = inject(Router);

  groups: MenuGroup[] = [
    {
      id: 'business',
      label: 'Business',
      icon: 'account_balance',
      items: [
        { label: 'Dashboard business', icon: 'dashboard', route: '/business' },
        { label: 'Factures clients', icon: 'request_quote', route: '/business/invoices' },
        { label: 'Devis', icon: 'description', route: '/business/quotes' },
        { label: 'Suivi des paiements', icon: 'payments', route: '/business/payments' },
        { label: 'Jours travaillés', icon: 'event_available', route: '/business/worked-days' },
        { label: 'Paramètres', icon: 'settings', route: '/business/settings', requiredRole: 'admin' }
      ]
    },
    {
      id: 'inventaire',
      label: 'Inventaire',
      icon: 'inventory_2',
      items: [
        { label: 'Dashboard inventaire', icon: 'dashboard', route: '/inventaire' },
        { label: 'Liste du matériel', icon: 'list', route: '/inventory' },
        { label: 'Scan factures matériel', icon: 'document_scanner', route: '/invoices', requiredRole: 'admin' },
        { label: 'Factures matériel', icon: 'receipt', route: '/invoices/list', requiredRole: 'admin' }
      ]
    },
    {
      id: 'work',
      label: 'Work',
      icon: 'hub',
      items: [
        { label: 'Dashboard work', icon: 'insights', route: '/work' },
        { label: 'Mind Map', icon: 'account_tree', route: '/mindmap', requiredRoles: ['admin', 'collaborator'] },
        { label: 'Dev Extensions', icon: 'extension', route: '/work/dev-extensions', requiredRoles: ['admin', 'collaborator'] },
        {
          label: 'Réseaux sociaux',
          icon: 'share',
          route: '/social-media',
          requiredRoles: ['admin', 'social_admin', 'social_approver', 'social_editor']
        }
      ]
    }
  ];

  get visibleGroups(): MenuGroup[] {
    return this.groups.filter(group => this.visibleItems(group).length > 0);
  }

  visibleItems(group: MenuGroup): MenuItem[] {
    return group.items.filter(item => this.canSee(item));
  }

  isGroupExpanded(group: MenuGroup): boolean {
    const url = this.router.url;
    return this.visibleItems(group).some(item =>
      url === item.route || url.startsWith(item.route + '/')
    );
  }

  hasRole(role: string): boolean {
    return this.authService.hasRole(role);
  }

  canSee(item: MenuItem): boolean {
    if (item.requiredRoles && item.requiredRoles.length > 0) {
      return item.requiredRoles.some(role => this.hasRole(role));
    }
    return !item.requiredRole || this.hasRole(item.requiredRole);
  }

  onMenuClick(): void {
    if (this.isHandset) {
      this.drawer.close();
    }
  }
}
